import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Box, Button, FormControlLabel, Snackbar, Switch, Tab, Tabs } from "@mui/material";

import ActionsTab from "./ActionsTab";
import HistoryTab from "./HistoryTab";
import OverviewTab from "./OverviewTab";
import RunActionDrawer from "./RunActionDrawer";
import RunDetailDrawer from "./RunDetailDrawer";
import SchedulesTab from "./SchedulesTab";
import { SYNC_ACTIONS } from "./syncActions";
import { isActiveState } from "./syncFormat";
import { LiveDot } from "./SyncParts";
import { SyncTasksProvider, useSyncTasks } from "./SyncTasksContext";
import TasksTab, { TaskDrawer } from "./TasksTab";
import { useSyncTaskNames } from "./useSyncTaskNames";

const ACCENT = "#26890d";
const TABS = ["overview", "actions", "schedules", "history", "tasks"];

const SyncOperations = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const tab = TABS.includes(requested) ? requested : "overview";
  const { tasks } = useSyncTasks();
  const [runActionId, setRunActionId] = useState(null);
  const [openTaskId, setOpenTaskId] = useState(null);
  const [openRunId, setOpenRunId] = useState(null);
  // Housekeeping runs (recovery.*, beat.*) are left out of the Overview numbers unless this is on. It sits in the tab bar
  // so it doesn't take a row of its own above the tiles.
  const [includeSystem, setIncludeSystem] = useState(false);
  const { hasSystemRuns } = useSyncTaskNames();
  const [toast, setToast] = useState(null); // { message, severity, taskLink }

  // Switching tab drops the other tab's own filters (h_*) so they don't follow you around.
  // `extra` carries a ready-made view, e.g. the history tab already filtered to failed runs.
  const go = (next, extra = {}) => setSearchParams(next === "overview" ? {} : { tab: next, ...extra }, { replace: true });
  const notify = (message, severity = "success", taskLink = false) => setToast({ message, severity, taskLink });
  const activeCount = tasks.filter((task) => isActiveState(task.state)).length;

  return (
    <div>
      <div className="ecr-ops-page-header">
        <p className="ecr-ops-page-header__eyebrow">ECR Ops Portal</p>
        <h1 className="ecr-ops-page-header__title">Sync Operations</h1>
        <p className="ecr-ops-page-header__sub">Run background syncs, watch them finish, and manage the schedules that run them automatically.</p>
      </div>

      <div className="ecr-ops-content">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, value) => go(value)}
          sx={{ minWidth: 0, "& .MuiTab-root": { textTransform: "none", fontWeight: 600 }, "& .Mui-selected": { color: `${ACCENT} !important` }, "& .MuiTabs-indicator": { backgroundColor: ACCENT } }}
        >
          <Tab value="overview" label="Overview" />
          <Tab value="actions" label={`Run actions (${SYNC_ACTIONS.length})`} />
          <Tab value="schedules" label="Schedules" />
          <Tab value="history" label="Run history" />
          <Tab
            value="tasks"
            label={
              activeCount ? (
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                  <LiveDot />
                  Task tracker ({activeCount} running)
                </span>
              ) : (
                "Task tracker"
              )
            }
          />
        </Tabs>
        {tab === "overview" && hasSystemRuns && (
          <FormControlLabel
            control={<Switch size="small" color="success" checked={includeSystem} onChange={(event) => setIncludeSystem(event.target.checked)} />}
            label={<span style={{ fontSize: 13 }}>Include system tasks</span>}
            title="Housekeeping that runs all the time, such as recovery.stale and the scheduler refresh. Hidden by default so the numbers are about the syncs."
            sx={{ m: 0 }}
          />
        )}
        </Box>

        {tab === "overview" && <OverviewTab onRun={setRunActionId} onGo={go} onOpenTask={setOpenTaskId} onOpenRun={setOpenRunId} includeSystem={includeSystem} />}
        {tab === "actions" && <ActionsTab onRun={setRunActionId} />}
        {tab === "schedules" && <SchedulesTab onNotify={notify} />}
        {tab === "history" && <HistoryTab onOpenRun={setOpenRunId} />}
        {tab === "tasks" && <TasksTab onOpenTask={setOpenTaskId} />}
      </div>

      {runActionId && (
        <RunActionDrawer
          actionId={runActionId}
          onClose={() => setRunActionId(null)}
          onQueued={({ title }) => notify(`Queued: ${title}. Accepted means queued, not finished.`, "success", true)}
        />
      )}
      {openTaskId && <TaskDrawer taskId={openTaskId} onClose={() => setOpenTaskId(null)} />}
      {openRunId && <RunDetailDrawer taskId={openRunId} onClose={() => setOpenRunId(null)} />}

      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        {toast ? (
          <Alert
            severity={toast.severity}
            onClose={() => setToast(null)}
            action={toast.taskLink ? <Button color="inherit" size="small" onClick={() => { go("tasks"); setToast(null); }}>Task tracker</Button> : undefined}
          >
            {toast.message}
          </Alert>
        ) : <span />}
      </Snackbar>
    </div>
  );
};

const SyncOperationsPage = () => (
  <SyncTasksProvider>
    <SyncOperations />
  </SyncTasksProvider>
);

export default SyncOperationsPage;
